import { EFinancingProgram } from '@if/core/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import {
  EditIfXpressWorksheetDto,
  IfXpressWorksheet,
  IfXpressWorksheetDecisionDTO,
  IfXpressWorksheetRevision,
} from '@src/data/types/IfXpressWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const baseUrl = '/Applications/ifxpress/{creditApplicationId}/Worksheet'
const computeFundingUrl = 'Applications/ComputeFunding'

// Query Keys
export const ifXpressWorksheetKeys = {
  all: ['ifXpressWorksheet'] as const,
  revisions: (creditApplicationId: string) =>
    [...ifXpressWorksheetKeys.all, 'revisions', creditApplicationId] as const,
}

// API Functions
export async function createIfXpressWorksheet(data: EditIfXpressWorksheetDto): Promise<IfXpressWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<IfXpressWorksheet>(url, data)
  return response.data
}

export async function updateIfXpressWorksheet(data: EditIfXpressWorksheetDto): Promise<IfXpressWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<IfXpressWorksheet>(url, data)
  return response.data
}

export async function computeIfXpressFunding(data: FundingInfoDto): Promise<FundingComputedDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<FundingComputedDto>(computeFundingUrl, data)
  return response.data
}

export async function updateIfXpressWorksheetDecision(
  data: IfXpressWorksheetDecisionDTO,
): Promise<IfXpressWorksheet> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Decision`.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<IfXpressWorksheet>(url, data)
  return response.data
}

export async function getIfXpressWorksheetRevisions(
  creditApplicationId: string,
): Promise<IfXpressWorksheetRevision[]> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Revisions`.replace('{creditApplicationId}', creditApplicationId)
  const response = await apiClient.get<IfXpressWorksheetRevision[]>(url)
  return response.data
}

// React-Query Hooks
export function useGetIfXpressWorksheetRevisions(
  creditApplicationId: string | undefined,
): [IfXpressWorksheetRevision[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: ifXpressWorksheetKeys.revisions(creditApplicationId ?? ''),
    queryFn: () => getIfXpressWorksheetRevisions(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}

export function useCreateIfXpressWorksheet(): [MutationFunction<IfXpressWorksheet, EditIfXpressWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createIfXpressWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(createdWorksheet.creditApplicationId, EFinancingProgram.IfXpress)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: ifXpressWorksheetKeys.revisions(createdWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateIfXpressWorksheet(): [MutationFunction<IfXpressWorksheet, EditIfXpressWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateIfXpressWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.IfXpress)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: ifXpressWorksheetKeys.revisions(updatedWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useComputeIfXpressFunding(): [MutationFunction<FundingComputedDto, FundingInfoDto>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: computeIfXpressFunding,
  })

  return [mutateAsync, isPending]
}

export function useUpdateIfXpressWorksheetDecision(): [
  MutationFunction<IfXpressWorksheet, IfXpressWorksheetDecisionDTO>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateIfXpressWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.IfXpress)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: ifXpressWorksheetKeys.revisions(updatedWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

